import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";

const HomePlayersList = () => {
  const { turnirSlug } = useParams();
  const [searchParams] = useSearchParams();
  const urlPin = searchParams.get("pin");

  const [turnir, setTurnir] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [enteredPin, setEnteredPin] = useState(urlPin || "");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [players, setPlayers] = useState([{ no: "", name: "" }]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedPlayers, setSubmittedPlayers] = useState([]);

  useEffect(() => {
    const loadTournament = async () => {
      try {
        const { data: tournament, error } = await supabase
          .from("admin_turniri")
          .select("*")
          .eq("slug", turnirSlug)
          .single();

        if (error || !tournament) {
          setLoading(false);
          return;
        }

        setTurnir(tournament);

        const { data: teamData } = await supabase
          .from("turniri_timovi")
          .select("*")
          .eq("turnir_id", tournament.id)
          .eq("roster_submitted", false);

        setTeams(teamData || []);

        if (urlPin && urlPin === tournament.roster_pin) {
          setIsAuthorized(true);
        }

        const savedRoster = localStorage.getItem(
          `submitted_roster_${tournament.id}`,
        );
        if (localStorage.getItem(`roster_sent_${tournament.id}`)) {
          setSuccess(true);
          if (savedRoster) {
            try {
              setSubmittedPlayers(JSON.parse(savedRoster));
            } catch (e) {
              console.error(e);
            }
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadTournament();
  }, [turnirSlug, urlPin]);

  const handlePinSubmit = (e) => {
    e.preventDefault();

    if (enteredPin === turnir?.roster_pin) {
      setIsAuthorized(true);
    } else {
      alert("Netočan PIN!");
      setEnteredPin("");
    }
  };

  const handlePlayerChange = (index, field, value) => {
    if (field === "name" && value.length > 30) return;

    setPlayers((current) =>
      current.map((player, i) =>
        i === index ? { ...player, [field]: value } : player,
      ),
    );
  };

  const addPlayer = () => {
    const max = turnir?.max_igraca || 12;

    if (players.length >= max) {
      alert(`Maksimalan broj igrača je ${max}.`);
      return;
    }

    setPlayers((current) => [...current, { no: "", name: "" }]);
  };

  const removePlayer = (index) => {
    setPlayers((current) => current.filter((_, i) => i !== index));
  };

  const formatName = (name) => {
    const parts = name.trim().split(/\s+/);

    if (!parts.length || !name.trim()) return "";

    return parts
      .map((part, index) =>
        index === parts.length - 1
          ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          : `${part.charAt(0).toUpperCase()}.`,
      )
      .join(" ");
  };

  const submitRoster = async () => {
    if (!selectedTeamId) {
      alert("Odaberite ekipu!");
      return;
    }

    const validPlayers = players.filter((player) => player.name.trim());

    if (!validPlayers.length) {
      alert("Unesite barem jednog igrača s imenom!");
      return;
    }

    setSubmitting(true);

    try {
      const { data: currentTeam, error: checkError } = await supabase
        .from("turniri_timovi")
        .select("roster_submitted")
        .eq("id", selectedTeamId)
        .single();

      if (checkError || !currentTeam) {
        throw new Error("Greška pri provjeri statusa tima.");
      }

      if (currentTeam.roster_submitted) {
        alert("Ova ekipa je već poslala svoj popis igrača!");
        window.location.reload();
        return;
      }

      let nextAutoNo = 1;
      const assignedNumbersSet = new Set();

      validPlayers.forEach((p) => {
        if (p.no !== "" && p.no !== null && p.no !== undefined) {
          assignedNumbersSet.add(parseInt(p.no));
        }
      });

      const formattedPlayers = validPlayers.map((player) => {
        let finalNo;
        if (player.no !== "" && player.no !== null && player.no !== undefined) {
          finalNo = parseInt(player.no);
        } else {
          while (assignedNumbersSet.has(nextAutoNo)) {
            nextAutoNo++;
          }
          finalNo = nextAutoNo;
          assignedNumbersSet.add(nextAutoNo);
          nextAutoNo++;
        }

        return {
          no: finalNo,
          name: formatName(player.name),
          goals: 0,
        };
      });

      const { error } = await supabase
        .from("turniri_timovi")
        .update({
          players: formattedPlayers,
          roster_submitted: true,
        })
        .eq("id", selectedTeamId);

      if (error) throw error;

      localStorage.setItem(`roster_sent_${turnir.id}`, "true");
      localStorage.setItem(
        `submitted_roster_${turnir.id}`,
        JSON.stringify(formattedPlayers),
      );
      setSubmittedPlayers(formattedPlayers);
      setSuccess(true);
    } catch (error) {
      alert("Greška kod slanja: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTeam = teams.find((team) => team.id === selectedTeamId);
  const maxPlayers = turnir?.max_igraca || 12;
  const validPlayers = players.filter((player) => player.name.trim());

  if (loading) {
    return (
      <div className="players-page">
        <div className="loader" />
        <span>Učitavanje...</span>
      </div>
    );
  }

  if (!turnir) {
    return (
      <div className="players-page">
        <div className="simple-card">
          <div className="status-icon error">!</div>
          <h2>Turnir nije pronađen</h2>
          <p>Provjerite poveznicu i pokušajte ponovno.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="players-page">
        <div className="simple-card success-card">
          <div className="status-icon success">✓</div>

          <span className="status-label">USPJEŠNO</span>

          <h2>Popis je poslan!</h2>

          <p>
            Popis igrača za <strong>{turnir.naziv}</strong> uspješno je
            spremljen.
          </p>

          {submittedPlayers.length > 0 && (
            <div className="preview submitted-summary">
              <div className="preview-title">
                <span>POSLANI POPIS IGRAČA</span>
                <b>{submittedPlayers.length} igrača</b>
              </div>
              {submittedPlayers.map((player, index) => (
                <div className="preview-player" key={index}>
                  <strong>#{player.no}</strong>
                  <span>{player.name}</span>
                </div>
              ))}
            </div>
          )}

          <small>FucaScore</small>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="players-page">
        <div className="brand">
          <span />
          FUCASCORE
        </div>
        <div className="pin-card">
          {turnir.logo_url ? (
            <img
              src={turnir.logo_url}
              alt={turnir.naziv}
              className="pin-logo"
            />
          ) : (
            <div className="pin-logo fallback">FS</div>
          )}

          <h1>{turnir.naziv}</h1>

          <p>Unesite PIN za unos službenog popisa igrača.</p>

          <form onSubmit={handlePinSubmit}>
            <label>PRISTUPNI PIN</label>

            <input
              type="password"
              inputMode="numeric"
              maxLength="4"
              placeholder="••••"
              value={enteredPin}
              onChange={(e) =>
                setEnteredPin(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
            />

            <button disabled={enteredPin.length !== 4}>
              Otključaj pristup
              <span>→</span>
            </button>
          </form>

          <small>Powered by FucaScore</small>
        </div>
      </div>
    );
  }

  return (
    <div className="players-page">
      <main className="players-card">
        <header className="players-header">
          {turnir.logo_url ? (
            <img
              src={turnir.logo_url}
              alt={turnir.naziv}
              className="tournament-logo"
            />
          ) : (
            <div className="tournament-logo fallback">FS</div>
          )}

          <div>
            <span>FUCASCORE</span>
            <h1>{turnir.naziv}</h1>
            <p>Unos popisa igrača</p>
          </div>
        </header>

        <section className="team-select">
          <label>ODABERI EKIPU</label>
          <select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
          >
            <option value="">Odaberite ekipu:</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </section>

        {selectedTeam ? (
          <>
            <div className="selected-team-info">
              <h2>{selectedTeam.name}</h2>
              {selectedTeam.group_name && (
                <span className="group-subtext">
                  Grupa {selectedTeam.group_name}
                </span>
              )}
            </div>

            <section className="roster">
              <div className="roster-title">
                <span>POPIS IGRAČA</span>
                <b>
                  {validPlayers.length} / {maxPlayers}
                </b>
              </div>

              <div className="players-list">
                {players.map((player, index) => (
                  <div className="player-row" key={index}>
                    <input
                      type="number"
                      placeholder="#"
                      value={player.no}
                      onChange={(e) =>
                        handlePlayerChange(index, "no", e.target.value)
                      }
                    />

                    <div className="input-with-counter">
                      <input
                        type="text"
                        placeholder={`Igrač ${index + 1} (Ime i prezime)`}
                        value={player.name}
                        maxLength={30}
                        onChange={(e) =>
                          handlePlayerChange(index, "name", e.target.value)
                        }
                      />
                      <span className="char-counter">
                        {player.name.length}/30
                      </span>
                    </div>

                    {players.length > 1 && (
                      <button
                        type="button"
                        className="remove"
                        onClick={() => removePlayer(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {players.length < maxPlayers && (
                <button
                  type="button"
                  className="add-player"
                  onClick={addPlayer}
                >
                  + Dodaj igrača
                </button>
              )}

              {validPlayers.length > 0 && (
                <div className="preview">
                  <div className="preview-title">
                    <span>PREVIEW POPISA</span>
                    <b>{validPlayers.length} igrača</b>
                  </div>
                  {(() => {
                    // 1. Prikupi sve ručno upisane brojeve koje su igrači već definirali
                    const assignedNumbers = new Set();
                    validPlayers.forEach((p) => {
                      if (p.no !== "" && p.no !== null && p.no !== undefined) {
                        assignedNumbers.add(parseInt(p.no));
                      }
                    });

                    let nextAutoNo = 1;

                    // 2. Mapiraj igrače i dodijeli automatske brojeve onima koji nemaju unesen broj
                    return validPlayers.map((player, index) => {
                      let previewNo = player.no;
                      if (
                        previewNo === "" ||
                        previewNo === null ||
                        previewNo === undefined
                      ) {
                        while (assignedNumbers.has(nextAutoNo)) {
                          nextAutoNo++;
                        }
                        previewNo = nextAutoNo;
                        assignedNumbers.add(nextAutoNo);
                        nextAutoNo++;
                      }

                      return (
                        <div className="preview-player" key={index}>
                          <strong>#{previewNo}</strong>
                          <span>{formatName(player.name)}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              <button
                className="submit"
                onClick={submitRoster}
                disabled={submitting || validPlayers.length === 0}
              >
                {submitting ? "Slanje..." : "Pošalji popis"}
                {!submitting && <span>→</span>}
              </button>
            </section>
          </>
        ) : (
          <div className="empty-state">
            <h3>Niste odabrali ekipu</h3>
            <p>Molimo odaberite ekipu iz padajućeg izbornika iznad.</p>
          </div>
        )}
      </main>

      <footer>
        Powered by <strong>FucaScore</strong>
      </footer>
    </div>
  );
};

export default HomePlayersList;
